import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import GenericStepperDialog from "./GenericStepperDialog";
import styles from "./zeroTrustPolicies.module.scss";

const policyTypes = [
  {
    title: "User-based Policy",
    description: "Control based on user identity and role",
  },
  {
    title: "Device-based Policy",
    description: "Control based on device type and health",
  },
  {
    title: "OT/ICS Policy",
    description: "Control for operational technology",
  },
  {
    title: "Network-based Policy",
    description: "Control based on network zones",
  },
];

const CreatePolicyDialog = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [policyType, setPolicyType] = useState(policyTypes[0].title);
  const [policyName, setPolicyName] = useState("");
  const [scope, setScope] = useState("Corporate Network");
  const [sourceRestriction, setSourceRestriction] =
    useState("Select source...");
  const [timeBusinessHours, setTimeBusinessHours] = useState(true);
  const [timeExcludeWeekends, setTimeExcludeWeekends] = useState(false);
  const [accessAction, setAccessAction] = useState("Allow Access");
  const [additionalActions, setAdditionalActions] = useState([
    "Send alert to SOC team",
  ]);
  const [enforcementMode, setEnforcementMode] = useState(
    "Simulation Mode (no enforcement)"
  );

  const steps = useMemo(
    () => [
      { label: "Policy Type", icon: SecurityOutlinedIcon },
      { label: "Conditions & Context", icon: TuneOutlinedIcon },
      { label: "Access Rules", icon: LockOutlinedIcon },
      { label: "Simulation & Impact", icon: VisibilityOutlinedIcon },
      { label: "Review & Enforce", icon: CheckCircleOutlineOutlinedIcon },
    ],
    []
  );

  const handleClose = () => {
    onClose();
    setActiveStep(0);
    setPolicyName("");
    setPolicyType(policyTypes[0].title);
    setScope("Corporate Network");
    setSourceRestriction("Select source...");
    setTimeBusinessHours(true);
    setTimeExcludeWeekends(false);
    setAccessAction("Allow Access");
    setAdditionalActions(["Send alert to SOC team"]);
    setEnforcementMode("Simulation Mode (no enforcement)");
  };

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const footer = (
    <Box className={styles.stepperFooterRow}>
      <Button className={styles.stepperCancel} onClick={handleClose}>
        Cancel
      </Button>
      <Box className={styles.stepperFooterActions}>
        {activeStep > 0 ? (
          <Button
            variant="outlined"
            className={styles.stepperSecondary}
            onClick={handleBack}
            startIcon={<ArrowBackIosNewRoundedIcon fontSize="small" />}
          >
            Back
          </Button>
        ) : null}
        <Button
          variant="contained"
          className={styles.stepperPrimary}
          onClick={activeStep === steps.length - 1 ? handleClose : handleNext}
          endIcon={<ArrowForwardIosRoundedIcon fontSize="small" />}
        >
          {activeStep === steps.length - 1 ? "Enforce Policy" : "Next"}
        </Button>
      </Box>
    </Box>
  );

  return (
    <GenericStepperDialog
      open={open}
      onClose={handleClose}
      title="Create Zero Trust Policy"
      subtitle="Step-by-step policy configuration"
      steps={steps}
      activeStep={activeStep}
      footer={footer}
    >
      {activeStep === 0 ? (
        <Box className={styles.stepperSection}>
          <Typography className={styles.stepperSectionTitle}>
            Select Policy Type
          </Typography>
          <Box className={styles.policyTypeGrid}>
            {policyTypes.map((item) => (
              <button
                type="button"
                key={item.title}
                className={`${styles.policyTypeCard} ${
                  policyType === item.title ? styles.policyTypeSelected : ""
                }`}
                onClick={() => setPolicyType(item.title)}
              >
                <Typography className={styles.policyTypeTitle} fontWeight={700}>
                  {item.title}
                </Typography>
                <Typography className={styles.policyTypeDescription}>
                  {item.description}
                </Typography>
              </button>
            ))}
          </Box>
          <Box className={styles.stepperInputBlock}>
            <Typography className={styles.stepperInputLabel}>
              Policy Name
            </Typography>
            <TextField
              value={policyName}
              onChange={(event) => setPolicyName(event.target.value)}
              placeholder="e.g. OT After-hours Write Block"
              fullWidth
              sx={{ borderRadius: "10px" }}
              size="small"
              className={styles.stepperTextField}
            />
          </Box>
        </Box>
      ) : null}

      {activeStep === 1 ? (
        <Box className={styles.stepperSection}>
          <Typography className={styles.stepperSectionTitle}>
            Define Conditions & Context
          </Typography>
          <Box className={styles.stepperInputBlock}>
            <Typography className={styles.stepperInputLabel}>Scope</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={scope}
                onChange={(event) => setScope(event.target.value)}
                className={styles.stepperSelect}
              >
                {[
                  "Corporate Network",
                  "OT Production Network",
                  "SCADA Systems",
                  "Remote Access",
                ].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.stepperInputBlock}>
            <Typography className={styles.stepperInputLabel}>
              Time Restrictions
            </Typography>
            <Box className={styles.timeRestrictionGrid}>
              <button
                type="button"
                className={`${styles.timeRestrictionCard} ${
                  timeBusinessHours ? styles.timeRestrictionActive : ""
                }`}
                onClick={() => setTimeBusinessHours((prev) => !prev)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={timeBusinessHours}
                      onChange={() => setTimeBusinessHours((prev) => !prev)}
                    />
                  }
                  label="Business Hours Only"
                />
                <Typography className={styles.timeRestrictionHint}>
                  6:00 AM - 6:00 PM
                </Typography>
              </button>
              <button
                type="button"
                className={`${styles.timeRestrictionCard} ${
                  timeExcludeWeekends ? styles.timeRestrictionActive : ""
                }`}
                onClick={() => setTimeExcludeWeekends((prev) => !prev)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={timeExcludeWeekends}
                      onChange={() => setTimeExcludeWeekends((prev) => !prev)}
                    />
                  }
                  label="Exclude Weekends"
                />
                <Typography className={styles.timeRestrictionHint}>
                  Monday - Friday
                </Typography>
              </button>
            </Box>
          </Box>

          <Box className={styles.stepperInputBlock}>
            <Typography className={styles.stepperInputLabel}>
              Source Restrictions
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={sourceRestriction}
                onChange={(event) => setSourceRestriction(event.target.value)}
                className={styles.stepperSelect}
              >
                {[
                  "Select source...",
                  "Trusted Internal Networks",
                  "External Vendors",
                  "Remote Admin Zones",
                  "Unknown Sources",
                ].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      ) : null}

      {activeStep === 2 ? (
        <Box className={styles.stepperSection}>
          <Typography className={styles.stepperSectionTitle}>
            Configure Access Rules
          </Typography>
          <Box className={styles.stepperInputBlock}>
            <Typography className={styles.stepperInputLabel}>
              Action to Take
            </Typography>
            <RadioGroup
              value={accessAction}
              onChange={(event) => setAccessAction(event.target.value)}
              className={styles.actionList}
            >
              {["Allow Access", "Deny Access", "Quarantine", "Alert Only"].map(
                (item) => (
                  <FormControlLabel
                    key={item}
                    value={item}
                    control={<Radio />}
                    label={item}
                    className={styles.actionListItem}
                  />
                )
              )}
            </RadioGroup>
          </Box>
          <Box className={styles.stepperInputBlock}>
            <Typography className={styles.stepperInputLabel}>
              Additional Actions
            </Typography>
            <Box className={styles.additionalActions}>
              {[
                "Send alert to SOC team",
                "Log to blockchain audit trail",
                "Trigger automated investigation",
              ].map((item) => (
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      checked={additionalActions.includes(item)}
                      onChange={(event) => {
                        setAdditionalActions((prev) => {
                          if (event.target.checked) {
                            return [...prev, item];
                          }
                          return prev.filter((entry) => entry !== item);
                        });
                      }}
                    />
                  }
                  label={item}
                  className={styles.additionalActionItem}
                />
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}

      {activeStep === 3 ? (
        <Box className={styles.stepperSection}>
          <Typography className={styles.stepperSectionTitle}>
            Simulation & Impact Analysis
          </Typography>
          <Box className={styles.impactCard}>
            <Typography className={styles.impactTitle}>
              Estimated Impact
            </Typography>
            <Box className={styles.impactGrid}>
              <Box>
                <Typography className={styles.impactLabel}>
                  Affected Assets
                </Typography>
                <Typography className={styles.impactValue}>47</Typography>
              </Box>
              <Box>
                <Typography className={styles.impactLabel}>
                  Blocked Actions (24h)
                </Typography>
                <Typography className={styles.impactValueWarning}>
                  ~230
                </Typography>
              </Box>
              <Box>
                <Typography className={styles.impactLabel}>
                  Risk Reduction
                </Typography>
                <Typography className={styles.impactValuePositive}>
                  -12%
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box className={styles.stepperInputBlock}>
            <Typography className={styles.stepperInputLabel}>
              Enforcement Mode
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={enforcementMode}
                onChange={(event) => setEnforcementMode(event.target.value)}
                className={styles.stepperSelect}
              >
                {[
                  "Simulation Mode (no enforcement)",
                  "Enforce with Alerts Only",
                  "Full Enforcement",
                ].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button className={styles.runSimulationButton}>
            Run 7-Day Simulation
          </Button>
        </Box>
      ) : null}

      {activeStep === 4 ? (
        <Box className={styles.stepperSection}>
          <Typography className={styles.stepperSectionTitle}>
            Review & Enforce
          </Typography>
          <Box className={styles.reviewSummaryCard}>
            <Box className={styles.reviewSummaryHeader}>
              <Typography className={styles.stepperReviewTitle}>
                {policyName || "New Policy"}
              </Typography>
            </Box>
            <Box className={styles.reviewSummaryGrid}>
              <Box>
                <Typography className={styles.reviewLabel}>Type</Typography>
                <Typography className={styles.reviewValue}>
                  {policyType}
                </Typography>
              </Box>
              <Box>
                <Typography className={styles.reviewLabel}>Scope</Typography>
                <Typography className={styles.reviewValue}>{scope}</Typography>
              </Box>
            </Box>
          </Box>
          <Box className={styles.reviewWarning}>
            <Typography className={styles.reviewWarningTitle}>
              Important:
            </Typography>
            <Typography className={styles.reviewWarningText}>
              This policy will be enforced immediately and will affect 47
              assets. Make sure all configurations are correct before
              proceeding.
            </Typography>
          </Box>
        </Box>
      ) : null}
    </GenericStepperDialog>
  );
};

export default CreatePolicyDialog;
